import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
	IconButton,
	Menu,
	MenuItem,
	Badge,
	Box,
	CircularProgress,
	Avatar,
	ListItemText,
	ListItemAvatar,
	Alert,
	Skeleton,
} from '@mui/material'
import {
	Person as PersonIcon,
	Email as EmailIcon,
	Warning as WarningIcon,
	CheckCircle as CheckCircleIcon,
	Assignment as AssignmentIcon,
	AccountBalance as AccountBalanceIcon,
	HourglassEmpty as HourglassEmptyIcon,
	Cancel as CancelIcon,
} from '@mui/icons-material'
import {
	getV1Notification,
	getV1NotificationMarkAllAsRead,
	patchV1NotificationMarkAsReadId,
} from '../../../../api-client/api.system-feature/api'
import { getErrorMessage, getToken } from '../../../../utils/common'
import formatText, { convertToUnderscore, formatLabel } from '../../../../utils/format-text'
import Typography from '../../../typography/typography'
import images from '../../../../assets/images'
import { generateRouteWithId } from '../../../../utils/route-utils'
import { PathnameGenerator, PathnameAccounting } from '../../../../constants/pathnames/pathname.accounting'
import { ROUTES as RENTALROUTES } from '../../../../constants/pathnames/pathname.rental'

import { PathnameInventory, PathnameGenerator as PathnameGeneratorInventory } from '../../../../constants/pathnames/pathname.inventory'

import {
	PathnameManufacturing,
	PathnameGenerator as PathnameGeneratorManufacuring,
} from '../../../../constants/pathnames/pathname.manufacturing.ts'

import {
	PathnamePurchase,
	PathnameGenerator as PathnameGeneratorPurchase,
} from '../../../../constants/pathnames/pathname.procurement.ts'

import { PathnameCrm, PathnameGenerator as PathnameGeneratorCrm } from '../../../../constants/pathnames/pathname.crm.ts'

import { PathnameRental, PathnameGenerator as PathnameGeneratorRental } from '../../../../constants/pathnames/pathname.rental.ts'

import { useNavigate } from 'react-router-dom'
import { Notification, Tick } from '../../../icons'
import Button from '../../../button/button'
import { enqueueSnackbar } from 'notistack'

const getNotificationUrl = (data: any) => {
	const { moduleName = '', moduleId = '', notificationData = {} } = data
	const key = convertToUnderscore(moduleName)

	const routeMap: Record<string, () => string> = {
		sales_invoice: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_SALES_INVOICES), moduleId.toString()),

		asset_transfers: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_ASSET_TRANSFER), moduleId.toString()),

		budgets: () => generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_BUDGET), moduleId.toString()),

		cash_expense: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_CASH_EXPENSE), moduleId.toString()),

		credit_notes: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_CREDIT_NOTE), moduleId.toString()),

		debit_notes: () => generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_DEBIT_NOTE), moduleId.toString()),

		expense_reimbursement: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_EXPENSE_REIMBURSEMENT), moduleId.toString()),

		journal_entries: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_JOURNAL_ENTRY), moduleId.toString()),

		payment_entries: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_PAYMENT_ENTRY), moduleId.toString()),

		purchase_invoice: () =>
			generateRouteWithId(PathnameGenerator(PathnameAccounting.VIEW_PURCHASE_INVOICE), moduleId.toString()),

		stock_transfer: () =>
			generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_STOCK_TRANSFER), moduleId.toString()),

		bills_of_materials: () =>
			generateRouteWithId(
				PathnameGeneratorManufacuring(PathnameManufacturing.VIEW_BILL_OF_MATERIAL),
				moduleId.toString()
			),

		purchase_agreement: () =>
			generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_AGREEMENT), moduleId.toString()),

		purchase_orders: () =>
			generateRouteWithId(
				notificationData?.data?.is_rental == 1
					? RENTALROUTES.VIEW
					: PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER),
				moduleId.toString()
			),

		purchase_request: () =>
			generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER), moduleId.toString()),

		vra: () =>
			generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_VENDOR_RETURNS), moduleId.toString()),

		sales_customer_returns: () =>
			generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_CUSTOMER_RETURNS), moduleId.toString()),

		sales_order: () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_SALES_ORDER), moduleId.toString()),

		quotation: () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_QUOTATION), moduleId.toString()),

		rental_order: () =>
			generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_RENTAL_ORDER), moduleId.toString()),

		replacement_order: () =>
			generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_RENTAL_ORDER), moduleId.toString()),

		rental_agreement: () =>
			generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_AGREEMENT), moduleId.toString()),

		cross_hire_order: () =>
			generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_CROSS_HIRE_ORDER), moduleId.toString()),

		cross_hire_request: () =>
			generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_CROSS_HIRE_REQUEST), moduleId.toString()),
	}

	return routeMap[key]?.() || '/dashboard'
}

const mainModuleIcons = {
	rbac: images.user,
	document: images.document,
	'accounts-and-finance': images.accounting,
	inventory: images.inventory,
	manufacturing: images.manufacturing,
	sales: images.crm,
	rental: images.rental,
	user: images.user,
	purchase: images.procurement,
	hrms: images.hrms,
}
const mainModuleBgColors = {
	rbac: '#F3E5F5',
	document: '#FFF8E1',
	'accounts-and-finance': '#E8F5E8',
	inventory: '#E3F2FD',
	manufacturing: '#FFF3E0',
	sales: '#F3E5F5',
	rental: '#E8F5E8',
	user: '#F3E5F5',
	purchase: '#FCE4EC',
	hrms: '#fcfcfc',
}

const NotificationMenu = () => {
	const [anchorEl, setAnchorEl] = useState(null)
	const [notifications, setNotifications] = useState([])
	const [loading, setLoading] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [unreadCount, setUnreadCount] = useState(0)
	const [markingAllAsRead, setMarkingAllAsRead] = useState(false)
	const [pagination, setPagination] = useState({
		skip: 0,
		limit: 20,
		totalCount: 0,
		hasMore: true,
	})

	const navigate = useNavigate()

	const menuRef = useRef(null)
	const isLoadingMoreRef = useRef(false)
	const open = Boolean(anchorEl)

	// Transform API notification to display format
	const transformNotification = (apiNotification) => {
		const moduleIcons = {
			'accounts-and-finance': <AccountBalanceIcon color="primary" />,
			user: <PersonIcon color="action" />,
			approval: <AssignmentIcon color="warning" />,
		}

		const getInitials = (performedUser) => {
			if (!performedUser) return null
			const firstName = performedUser.first_name || ''
			const lastName = performedUser.last_name || ''
			if (!firstName && !lastName) return null
			return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
		}

		const getFullName = (performedUser) => {
			if (!performedUser) return null
			const firstName = performedUser.first_name || ''
			const lastName = performedUser.last_name || ''
			return `${firstName} ${lastName}`.trim() || null
		}

		const formatTimestamp = (dateString) => {
			const date = new Date(dateString)
			const now = new Date()
			const diffMs = now - date
			const diffMins = Math.floor(diffMs / 60000)
			const diffHours = Math.floor(diffMs / 3600000)
			const diffDays = Math.floor(diffMs / 86400000)

			if (diffMins < 1) return 'Just now'
			if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
			if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
			if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
			return date.toLocaleDateString()
		}

		const subData = apiNotification.sub_module_data || {}

		return {
			id: apiNotification.id,
			type: apiNotification.action || apiNotification.notification_type || 'info',
			title: formatText(apiNotification.title),
			message: apiNotification.message,
			timestamp: formatTimestamp(apiNotification.created_at),
			read: apiNotification.status === 'read',
			avatar: getInitials(apiNotification.performed_user),
			performedByName: getFullName(apiNotification.performed_user),
			module: apiNotification.module,
			subModule: apiNotification.sub_module,
			subModuleId: apiNotification.sub_module_id,
			status: apiNotification.status,
			seriesNumber: subData.series_number || null,
			documentStatus: subData.status || null,
			customerName: subData.customer_name || null,
			icon: moduleIcons[apiNotification.module] || moduleIcons[apiNotification.action] || <PersonIcon color="action" />,
		}
	}

	// Fetch notifications with pagination
	const fetchNotifications = useCallback(async (isLoadMore = false) => {
		// Prevent multiple simultaneous load more requests
		if (isLoadMore && isLoadingMoreRef.current) {
			return
		}

		if (isLoadMore) {
			isLoadingMoreRef.current = true
			setLoadingMore(true)
		} else {
			setLoading(true)
			// Reset pagination when loading fresh data
			setPagination((prev) => ({
				...prev,
				skip: 0,
				hasMore: true,
			}))
		}
		setError(null)

		try {
			const currentSkip = isLoadMore ? pagination.skip : 0
			const response = await getV1Notification({
				skip: currentSkip,
				limit: pagination.limit,
				...getToken(),
			})

			if (response.success && response.data) {
				const transformedNotifications = response.data.notifications.map(transformNotification)

				if (isLoadMore) {
					setNotifications((prev) => [...prev, ...transformedNotifications])
				} else {
					setNotifications(transformedNotifications)
				}

				// Update pagination
				const newSkip = currentSkip + response.data.notifications.length
				const hasMore = newSkip < response.data.pagination.totalCount

				setPagination((prev) => ({
					...prev,
					skip: newSkip,
					totalCount: response.data.pagination.totalCount,
					hasMore,
				}))

				// Calculate unread count (assuming 'pending' status means unread)
				if (!isLoadMore) {
					const unread = response.data.pagination.unreadCount
					setUnreadCount(unread)
				}
			} else {
				setUnreadCount(0)
				throw new Error(response.message || 'Failed to load notifications')
			}
		} catch (err) {
			setError('Failed to load notifications')
		} finally {
			setLoading(false)
			setLoadingMore(false)
			if (isLoadMore) {
				isLoadingMoreRef.current = false
			}
		}
	}, [pagination.limit, pagination.skip])

	// Handle scroll for infinite loading
	const handleScroll = useCallback(
		(event) => {
			const { scrollTop, scrollHeight, clientHeight } = event.target
			const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50 // Increased threshold

			if (isNearBottom && !isLoadingMoreRef.current && !loading && pagination.hasMore) {
				fetchNotifications(true)
			}
		},
		[fetchNotifications, loading, pagination.hasMore]
	)

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
		if (notifications.length === 0) {
			fetchNotifications()
		}
	}

	const handleClose = () => {
		setAnchorEl(null)

		// Don't clear notifications immediately to avoid flickering
		// They will be refreshed when menu is opened again
	}

	const handleNotificationClick = async (notificationId) => {
		// Mark notification as read
		const n = notifications.find((n) => {
			return n.id === notificationId
		})
		if (!n) return

		handleClose()

		if (n.status === 'pending') {
			await patchV1NotificationMarkAsReadId({
				id: notificationId,
				...getToken(),
			})
		}

		setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)))
		setUnreadCount((prev) => Math.max(0, prev - 1))
		const url = getNotificationUrl({
			moduleName: n.subModule,
			moduleId: n.subModuleId,
			notificationData: n,
		})
    setTimeout(() => {
      navigate(url)
    }, 300)
	}

	const getNotificationIcon = (notification) => {
		if (notification.icon) {
			return notification.icon
		}

		switch (notification.type) {
			case 'approval':
				return <AssignmentIcon color="warning" />
			case 'user':
				return <PersonIcon color="action" />
			case 'message':
				return <EmailIcon color="primary" />
			case 'warning':
				return <WarningIcon color="warning" />
			case 'success':
				return <CheckCircleIcon color="success" />
			default:
				return <PersonIcon color="action" />
		}
	}

	const handleMarkAllAsRead = useCallback(async () => {
		if (markingAllAsRead) return // Prevent multiple calls

		setMarkingAllAsRead(true)

		if (!unreadCount) {
			enqueueSnackbar('No unread notifications!')
			setMarkingAllAsRead(false)
			return
		}

		try {
			await getV1NotificationMarkAllAsRead({ ...getToken() })
			// Update local state immediately for better UX
			setNotifications((prev) => prev.map((n) => ({ ...n, read: true, status: 'read' })))
			setUnreadCount(0)
		} catch (error) {
			const m = getErrorMessage(error.message)
			enqueueSnackbar(m, { variant: 'error' })
		} finally {
			setMarkingAllAsRead(false)
		}
	}, [markingAllAsRead, unreadCount])

	// Remove the problematic useEffect that was causing extra API calls

  useEffect(() => {
    if(!open) {
      setTimeout(() => {
        setNotifications([])
      }, 500)
    }
  }, [open])

	const getPerformerLabel = (action: string) => {
		switch (action?.toLowerCase()) {
			case 'approved':
				return 'Approved by'
			case 'rejected':
			case 'reject':
				return 'Rejected by'
			default:
				return 'Request to'
		}
	}

	const getStatusIcon = (action: string) => {
		switch (action?.toLowerCase()) {
			case 'approved':
				return <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
			case 'rejected':
			case 'reject':
				return <CancelIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />
			default:
				return <HourglassEmptyIcon sx={{ fontSize: '0.9rem', color: 'warning.main' }} />
		}
	}

	return (
		<Box>
			<IconButton
				// size="small"
				aria-label="notifications"
				aria-controls={open ? 'notification-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
			>
				<Badge badgeContent={unreadCount} color="error">
					<Notification fontSize="medium" />
				</Badge>
			</IconButton>

			<Menu
				id="notification-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				ref={menuRef}
				slotProps={{
					paper: {
						sx: {
							overflow: 'visible',
							minWidth: 400,
							maxWidth: 450,
							maxHeight: 520,
							'&:before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								top: 0,
								right: 13,
								width: 10,
								height: 10,
								bgcolor: 'background.paper',
								transform: 'translateY(-50%) rotate(45deg)',
								zIndex: 0,
								border: '1px solid',
								borderColor: ({ palette }) => palette.grey[300],
							},
						},
					},
				}}
				sx={{
					'& .MuiList-root': {
						borderRadius: '8px',
						maxHeight: 520,
						overflowY: 'auto',
						padding: 0,
					},
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				onScroll={handleScroll}
			>
				{/* Header */}
				<Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
					<Typography type="h5" weight="bold" color="grey.1000">
						Notifications
					</Typography>
					{unreadCount > 0 && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
							<Typography type="s4" color="grey.800">
								You have {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
							</Typography>
							<Button
								variant="text"
								startIcon={
									markingAllAsRead ? (
										<CircularProgress size={15} color="inherit" />
									) : (
										<Tick sx={{ fontSize: '0.75rem !important' }} color="grey.900" />
									)
								}
								sx={{ p: 0 }}
								onClick={handleMarkAllAsRead}
								disabled={unreadCount <= 0 || markingAllAsRead}
							>
								<Typography type="s4" color="grey.900">
									Mark all as read
								</Typography>
							</Button>
						</Box>
					)}
				</Box>

				{/* Loading State */}
				{loading && notifications.length === 0 && (
					<>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1,
								justifyContent: 'center',
								minWidth: 400,
								p: 1,
								m: 1,
								backgroundColor: 'grey.200',
								borderRadius: '8px',
							}}
						>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Skeleton variant="rounded" width={40} height={40} />
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Skeleton variant="rounded" width={360} height={25} />
									<Skeleton variant="rounded" width={150} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
								</Box>
							</Box>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1,
								justifyContent: 'center',
								minWidth: 400,
								p: 1,
								m: 1,
								backgroundColor: 'grey.200',
								borderRadius: '8px',
							}}
						>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Skeleton variant="rounded" width={40} height={40} />
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Skeleton variant="rounded" width={360} height={25} />
									<Skeleton variant="rounded" width={150} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
								</Box>
							</Box>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 1,
								justifyContent: 'center',
								minWidth: 400,
								p: 1,
								m: 1,
								backgroundColor: 'grey.200',
								borderRadius: '8px',
							}}
						>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Skeleton variant="rounded" width={40} height={40} />
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Skeleton variant="rounded" width={360} height={25} />
									<Skeleton variant="rounded" width={150} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
									<Skeleton variant="rounded" width={360} height={11} />
								</Box>
							</Box>
						</Box>
					</>
				)}

				{/* Error State */}
				{error && (
					<Box sx={{ p: 2 }}>
						<Alert severity="error">{error}</Alert>
					</Box>
				)}

				{/* Notifications List */}
				{!loading && !error && notifications.length > 0 && (
					<Box sx={{ maxHeight: 420, overflowY: 'auto', p: 1 }} onScroll={handleScroll}>
						{notifications.map((notification) => (
							<div key={notification.id}>
								<MenuItem
									onClick={() => handleNotificationClick(notification.id)}
									sx={({ palette }) => ({
										py: 1,
										px: 1,
										backgroundColor: !notification.read ? palette.theme.grey[200] : 'transparent',
										border: `1px solid ${palette.grey[200]}`,
										marginBottom: '4px !important',
										'&:hover': {
											backgroundColor: palette.theme.grey[200],
										},
										alignItems: 'flex-start',
										minHeight: 'auto',
									})}
								>
									<ListItemAvatar sx={{ minWidth: 48, mt: 0 }}>
										{mainModuleIcons?.[notification.module] ? (
											<Avatar
												sx={{
													width: 40,
													height: 40,
													fontSize: '0.875rem',
													bgcolor: mainModuleBgColors[notification.module] || 'primary.main',
													color: 'white',
													borderRadius: '8px',
													boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
													'& img': {
														width: '28px',
														height: '28px',
														objectFit: 'contain',
													},
												}}
												src={mainModuleIcons?.[notification.module]}
											/>
										) : (
											<>
												{notification.avatar ? (
													<Avatar
														sx={{
															width: 40,
															height: 40,
															fontSize: '0.875rem',
															bgcolor: 'primary.main',
															color: 'white',
															borderRadius: '8px',
															boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
														}}
													>
														{notification.avatar}
													</Avatar>
												) : (
													<Avatar
														sx={{
															width: 36,
															height: 36,
															bgcolor: 'grey.100',
															color: 'grey.600',
															borderRadius: '8px',
															boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
														}}
													>
														{getNotificationIcon(notification)}
													</Avatar>
												)}
											</>
										)}
									</ListItemAvatar>
									<ListItemText
										sx={{ margin: 0, pr: 1 }}
										primary={
											// Line 1: title  seriesNumber  statusIcon
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
												<Typography type="s3" weight="bold" color="grey.1000">
													{notification.title}
												</Typography>
												{notification.seriesNumber && (
													<Typography type="s4" sx={{ color: 'text.secondary', fontWeight: 500 }}>
														{notification.seriesNumber}
													</Typography>
												)}
												{getStatusIcon(notification.type)}
											</Box>
										}
										secondary={
											<Box>
												{/* Line 2: Customer / Vendor label + name */}
												{notification.customerName && (
													<Typography type="s4" color="grey.800" sx={{ display: 'block', mb: 0.25 }}>
														<Typography component="span" type="s5" sx={{ fontSize: '0.7rem !important', color: 'text.secondary', mr: 0.5 }}>
															{notification.module === 'purchase' ? 'Vendor:' : 'Customer:'}
														</Typography>
														{notification.customerName}
													</Typography>
												)}
												{/* Line 3: Approved by {name}  ·  timestamp */}
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
													{notification.performedByName && (
														<Typography type="s5" sx={{ fontSize: '0.7rem !important', color: 'text.secondary' }}>
															{getPerformerLabel(notification.type)} {notification.performedByName}
														</Typography>
													)}
													{notification.performedByName && (
														<Box sx={{ height: 4, width: 4, borderRadius: '100%', backgroundColor: 'grey.400', flexShrink: 0 }} />
													)}
													<Typography type="s5" color="primary" sx={{ fontSize: '0.65rem !important' }}>
														{notification.timestamp}
													</Typography>
												</Box>
											</Box>
										}
									/>
									{!notification.read && (
										<Box
											sx={{
												width: 10,
												height: 10,
												borderRadius: '50%',
												backgroundColor: 'primary.main',
												flexShrink: 0,
												mt: 0.5,
											}}
										/>
									)}
								</MenuItem>
							</div>
						))}

						{/* Loading More Indicator */}
						{loadingMore && (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, px: 3 }}>
								<CircularProgress size={20} />
								<Typography variant="body2" sx={{ ml: 1.5, color: 'text.secondary' }}>
									Loading more...
								</Typography>
							</Box>
						)}

						{/* End of List Indicator */}
						{!pagination.hasMore && notifications.length > 0 && (
							<Box sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
								<Typography type="s3" color="grey.800" textAlign="center">
									No more notifications
								</Typography>
							</Box>
						)}
					</Box>
				)}

				{/* Empty State */}
				{!loading && !error && notifications.length === 0 && (
					<Box sx={{ p: 4, textAlign: 'center' }}>
						<Notification sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
						<Typography type="s1" color="grey.800" sx={{ mb: 1 }} textAlign="center">
							No notifications yet
						</Typography>
					</Box>
				)}
			</Menu>
		</Box>
	)
}

export { NotificationMenu };
export default NotificationMenu
